import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { GroupView } from "@/services/types";
import { groupService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";

interface ActivateModalProps {
  open: boolean;
  group: GroupView | null;
  onClose: () => void;
  onActivated: (groupId: string) => void;
}

/**
 * The explicit, irreversible activation confirmation. Activation generates all
 * monthly cycles, marks the reserved organizer cycle, opens the first auction and
 * permanently locks the structural configuration.
 */
export function ActivateModal({ open, group, onClose, onActivated }: ActivateModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (!open) {
      setActivated(false);
    }
  }, [open]);

  const filled = group?.tickets.filter((t) => Boolean(t.member)).length ?? 0;
  const organizerTicket = group?.tickets.find((t) => t.isOrganizer);
  const organizerAssigned = Boolean(organizerTicket?.member);
  const missing: string[] = [];
  if (group && filled < group.memberCount) missing.push(`${group.memberCount - filled} more member(s) to be enrolled`);
  if (!organizerAssigned) missing.push("an organizer assigned to a ticket");
  const ready = group !== null && missing.length === 0;

  const confirmActivate = async () => {
    if (!group || activating || !ready) return;
    setActivating(true);
    try {
      await groupService.activateGroup(group.id, "Staff User");
      setActivated(true);
      toast.success("Group activated", `${group.name} is now active. The first auction is available.`);
    } catch (err) {
      toast.error("Could not activate group", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setActivating(false);
    }
  };

  const handleClose = () => {
    if (activated) {
      setActivated(false);
      onClose();
      if (group) onActivated(group.id);
    } else {
      onClose();
    }
  };

  const handleViewCycles = () => {
    setActivated(false);
    onClose();
    if (group) {
      onActivated(group.id);
      navigate(`/groups/${group.id}`);
    }
  };

  const handleGoToAuction = () => {
    setActivated(false);
    onClose();
    if (group) {
      onActivated(group.id);
      navigate(`/auction`);
    }
  };

  if (activated) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        title={
          <span className="flex items-center gap-2 text-success-600 font-semibold">
            <i className="ri-checkbox-circle-fill text-lg" /> Group Activated
          </span>
        }
        description={group ? `${group.name} (${group.groupCode}) is now active.` : ""}
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                icon={<i className="ri-auction-line" />}
                onClick={handleGoToAuction}
              >
                Go to Auction
              </Button>
              <Button
                icon={<i className="ri-calendar-line" />}
                onClick={handleViewCycles}
              >
                View Cycles
              </Button>
            </div>
          </div>
        }
      >
        <div className="py-1 space-y-4">
          <div className="rounded-xl border border-success-200 bg-success-50/50 p-4 text-center">
            <div className="mx-auto mb-2.5 flex h-14 w-14 items-center justify-center rounded-full bg-success-100 text-success-600 ring-4 ring-success-50">
              <i className="ri-checkbox-circle-fill text-3xl" />
            </div>
            <h3 className="text-base font-bold text-foreground-900">Group Activated Successfully!</h3>
            <p className="mt-1 text-xs text-foreground-600">
              <span className="font-semibold text-foreground-800">{group?.name}</span> is now active. All monthly cycles have been created and the first cycle is ready.
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-3.5 space-y-2.5">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
              Activation Summary
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-md border border-background-200 bg-background-100/60 p-2.5">
                <span className="text-foreground-500 block">Monthly Cycles</span>
                <span className="font-semibold text-foreground-900 tnum text-sm">{group?.memberCount} Cycles</span>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100/60 p-2.5">
                <span className="text-foreground-500 block">Reserved Cycle</span>
                <span className="font-semibold text-foreground-900 tnum text-sm">Cycle #{group?.reservedCycle}</span>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100/60 p-2.5">
                <span className="text-foreground-500 block">First Auction</span>
                <span className="font-semibold text-success-600 text-sm">Cycle 1 Ready</span>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100/60 p-2.5">
                <span className="text-foreground-500 block">Status</span>
                <span className="inline-flex items-center gap-1 font-semibold text-success-600 text-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-success-600" /> Active
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Activate this group?"
      description={
        group
          ? ready
            ? `${group.name} (${group.groupCode}) — every ticket is filled and the organizer is assigned.`
            : `${group.name} (${group.groupCode}) — enrolment incomplete (${filled}/${group.memberCount} tickets filled).`
          : ""
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={activating}>
            Not yet
          </Button>
          <Button icon={<i className="ri-rocket-line" />} loading={activating} disabled={!ready} onClick={confirmActivate}>
            Activate group
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {!ready ? (
          <Alert variant="error" title="Requirements not met">
            This group cannot be activated yet: {missing.join(" and ")}. Please complete ticket enrolment first.
          </Alert>
        ) : null}
        <p className="text-sm text-foreground-700">
          Activating this group is <span className="font-semibold">permanent and cannot be undone</span>. Activation will:
        </p>
        <ul className="space-y-1.5 text-sm text-foreground-700">
          <li className="flex gap-2"><i className="ri-check-line mt-0.5 text-success-600" />Generate all {group?.memberCount ?? "—"} monthly cycles</li>
          <li className="flex gap-2"><i className="ri-check-line mt-0.5 text-success-600" />Mark cycle {group?.reservedCycle ?? "—"} as the reserved organizer cycle</li>
          <li className="flex gap-2"><i className="ri-check-line mt-0.5 text-success-600" />Make the first auction available</li>
          <li className="flex gap-2"><i className="ri-check-line mt-0.5 text-success-600" />Lock the installment amount, member count, duration and reserved cycle forever</li>
        </ul>
        <Alert variant="warning" title="This cannot be undone">
          Once real money is collected against a cycle, changing these figures would corrupt every calculation that follows. The winning bid and dividend arithmetic depends on them being fixed.
        </Alert>
      </div>
    </Modal>
  );
}